import React, { useState, useEffect, useCallback } from 'react';
import MainSidebar from '../../components/ui/MainSidebar';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import { clientService } from '../../services/clientService';
import { vehicleHistoryService } from '../../services/vehicleHistoryService';
import { vehicleService } from '../../services/vehicleService';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import VehicleFormUnified, { VEHICLE_FORM_DEFAULTS, validateVehicleFields } from '../../components/VehicleFormUnified';
import { useAuth } from '../../contexts/AuthContext';
import AdminResetPasswordModal from './components/AdminResetPasswordModal';


// ─── Helpers ────────────────────────────────────────────────────────────────
const formatPhone = (v) => v?.replace(/\D/g, '')?.slice(0, 10) || '';
const formatRFC = (v) => v?.toUpperCase()?.slice(0, 13) || '';
const formatCP = (v) => v?.replace(/\D/g, '')?.slice(0, 5) || '';

const EMPTY_CLIENT = {
  nombre: '', empresa: '', telefono: '', telefonoAlt: '',
  email: '', rfc: '', direccion: '', ciudad: '', estado: '', codigoPostal: ''
};

const EMPTY_HISTORY = {
  title: '', notes: '', type: 'DONE', status: 'OPEN', dueDate: ''
};

// ─── ClientInfoForm (exact replica of Reception's "Información del Cliente") ─
const ClientInfoForm = ({ data, onChange, errors = {}, readOnly = false }) => {
  const field = (label, key, opts = {}) => (
    <div className={opts?.full ? 'md:col-span-2' : ''}>
      <label className="block text-sm font-medium text-foreground mb-2">
        {label}{opts?.required && <span className="text-destructive"> *</span>}
      </label>
      <Input
        type={opts?.type || 'text'}
        placeholder={opts?.placeholder || ''}
        value={data?.[key] || ''}
        onChange={(e) => {
          if (readOnly) return;
          let val = e?.target?.value;
          if (opts?.fmt === 'phone') val = formatPhone(val);
          if (opts?.fmt === 'rfc') val = formatRFC(val);
          if (opts?.fmt === 'cp') val = formatCP(val);
          onChange(key, val);
        }}
        error={errors?.[key]}
        maxLength={opts?.maxLength}
        className="w-full"
        disabled={readOnly}
      />
      {errors?.[key] && <p className="text-sm text-destructive mt-1">{errors?.[key]}</p>}
      {opts?.hint && <p className="text-xs text-muted-foreground mt-1">{opts?.hint}</p>}
    </div>
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {field('Nombre Completo', 'nombre', { full: true, required: true, placeholder: 'Ej: Juan Pérez García' })}
      {field('Empresa (Opcional)', 'empresa', { full: true, placeholder: 'Ej: Transportes del Norte SA de CV' })}
      {field('Teléfono', 'telefono', { required: true, placeholder: '5512345678', fmt: 'phone', maxLength: 10 })}
      {field('Teléfono Alternativo (Opcional)', 'telefonoAlt', { placeholder: '5587654321', fmt: 'phone', maxLength: 10 })}
      {field('Correo Electrónico', 'email', { required: true, type: 'email', placeholder: 'cliente@ejemplo.com' })}
      {field('RFC (Opcional)', 'rfc', { placeholder: 'XAXX010101000', fmt: 'rfc', maxLength: 13, hint: 'Formato: 3-4 letras + 6 dígitos + 3 caracteres' })}
      {field('Dirección (Opcional)', 'direccion', { full: true, placeholder: 'Ej: Av. Insurgentes 1234' })}
      {field('Ciudad', 'ciudad', { placeholder: 'Ej: Ciudad de México' })}
      {field('Estado', 'estado', { placeholder: 'Ej: CDMX' })}
      {field('Código Postal', 'codigoPostal', { placeholder: '03100', fmt: 'cp', maxLength: 5 })}
    </div>
  );
};

// ─── Source Badge ─────────────────────────────────────────────────────────────
const SourceBadge = ({ sourceType, isAutoGenerated, title }) => {
  const getLabel = () => {
    if (title?.startsWith('Próximo:')) return 'PREVENTIVO';
    if (sourceType === 'WORK_ORDER') return 'OT';
    if (sourceType === 'QUOTE') return 'COT';
    if (sourceType === 'INSPECTION') return 'INS';
    return 'MANUAL';
  };

  const getStyle = () => {
    const label = getLabel();
    if (label === 'OT') return 'bg-blue-100 text-blue-700';
    if (label === 'COT') return 'bg-purple-100 text-purple-700';
    if (label === 'INS') return 'bg-orange-100 text-orange-700';
    if (label === 'PREVENTIVO') return 'bg-teal-100 text-teal-700';
    return 'bg-muted text-muted-foreground';
  };

  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${getStyle()}`}>
      {getLabel()}
    </span>
  );
};

// ─── Alert Dot ────────────────────────────────────────────────────────────────
const AlertDot = ({ status, alertReady }) => {
  if (alertReady) {
    return <span className="inline-block w-2 h-2 rounded-full bg-red-500 shrink-0 mt-1" title="Alerta activa" />;
  }
  if (status === 'CLOSED') {
    return <span className="inline-block w-2 h-2 rounded-full bg-green-500 shrink-0 mt-1" title="Cerrado" />;
  }
  return <span className="inline-block w-2 h-2 rounded-full bg-yellow-400 shrink-0 mt-1" title="Pendiente" />;
};

// ─── VehicleHistoryPanel ─────────────────────────────────────────────────────
const VehicleHistoryPanel = ({ vehicleId }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(EMPTY_HISTORY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!vehicleId) return;
    setLoading(true);
    const data = await vehicleHistoryService?.getByVehicleId(vehicleId);
    setEntries(data);
    setLoading(false);
  }, [vehicleId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!formData?.title?.trim()) return;
    setSaving(true);
    try {
      await vehicleHistoryService?.create({ ...formData, vehicleId });
      setFormData(EMPTY_HISTORY);
      setShowForm(false);
      await load();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const done = entries?.filter(e => e?.type === 'DONE');
  const pending = entries?.filter(e => e?.type === 'PENDING');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-foreground">Historial de la Unidad</h4>
        <Button variant="outline" size="sm" iconName="Plus" iconPosition="left" onClick={() => setShowForm(!showForm)}>
          Agregar Registro
        </Button>
      </div>
      {showForm && (
        <div className="bg-muted/30 rounded-lg border border-border p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Título *</label>
              <Input
                type="text"
                placeholder="Ej: Cambio de aceite"
                value={formData?.title}
                onChange={(e) => setFormData(p => ({ ...p, title: e?.target?.value }))}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Tipo</label>
              <select
                value={formData?.type}
                onChange={(e) => setFormData(p => ({ ...p, type: e?.target?.value }))}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="DONE">Hecho</option>
                <option value="PENDING">Pendiente</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-foreground mb-1">Notas</label>
              <textarea
                value={formData?.notes}
                onChange={(e) => setFormData(p => ({ ...p, notes: e?.target?.value }))}
                rows={2}
                placeholder="Descripción adicional..."
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            </div>
            {formData?.type === 'PENDING' && (
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Fecha límite</label>
                <Input
                  type="date"
                  value={formData?.dueDate}
                  onChange={(e) => setFormData(p => ({ ...p, dueDate: e?.target?.value }))}
                  className="w-full"
                />
              </div>
            )}
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => { setShowForm(false); setFormData(EMPTY_HISTORY); }}>Cancelar</Button>
            <Button variant="default" size="sm" loading={saving} onClick={handleSave}>Guardar</Button>
          </div>
        </div>
      )}
      {loading ? (
        <p className="text-sm text-muted-foreground">Cargando historial...</p>
      ) : (
        <div className="space-y-4">
          {/* DONE */}
          <div>
            <p className="text-xs font-semibold text-success uppercase tracking-wide mb-2">Historial Hecho ({done?.length})</p>
            {done?.length === 0 ? (
              <p className="text-xs text-muted-foreground">Sin registros completados.</p>
            ) : (
              <div className="space-y-2">
                {done?.map(e => (
                  <div key={e?.id} className="flex items-start gap-2 p-2 rounded-lg bg-success/5 border border-success/20">
                    <AlertDot status={e?.status} alertReady={e?.alertReady} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="text-sm font-medium text-foreground">{e?.title}</p>
                        <SourceBadge sourceType={e?.sourceType} isAutoGenerated={e?.isAutoGenerated} title={e?.title} />
                        {e?.isAutoGenerated && (
                          <span className="inline-flex items-center px-1 py-0.5 rounded text-[9px] text-muted-foreground bg-muted">AUTO</span>
                        )}
                      </div>
                      {e?.notes && <p className="text-xs text-muted-foreground">{e?.notes}</p>}
                      {e?.serviceKmAtTime && <p className="text-xs text-muted-foreground">KM al servicio: {e?.serviceKmAtTime?.toLocaleString()}</p>}
                      <p className="text-xs text-muted-foreground">{new Date(e.createdAt)?.toLocaleDateString('es-MX')}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* PENDING */}
          <div>
            <p className="text-xs font-semibold text-warning uppercase tracking-wide mb-2">Pendientes ({pending?.length})</p>
            {pending?.length === 0 ? (
              <p className="text-xs text-muted-foreground">Sin pendientes.</p>
            ) : (
              <div className="space-y-2">
                {pending?.map(e => (
                  <div key={e?.id} className={`flex items-start gap-2 p-2 rounded-lg border ${
                    e?.alertReady
                      ? 'bg-red-50 border-red-200' :'bg-warning/5 border-warning/20'
                  }`}>
                    <AlertDot status={e?.status} alertReady={e?.alertReady} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="text-sm font-medium text-foreground">{e?.title}</p>
                        <SourceBadge sourceType={e?.sourceType} isAutoGenerated={e?.isAutoGenerated} title={e?.title} />
                        {e?.alertReady && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700">
                            <Icon name="AlertTriangle" size={9} /> ALERTA
                          </span>
                        )}
                        {e?.isAutoGenerated && (
                          <span className="inline-flex items-center px-1 py-0.5 rounded text-[9px] text-muted-foreground bg-muted">AUTO</span>
                        )}
                      </div>
                      {e?.notes && <p className="text-xs text-muted-foreground">{e?.notes}</p>}
                      {e?.nextServiceKm && <p className="text-xs text-muted-foreground">Próximo KM: {e?.nextServiceKm?.toLocaleString()}</p>}
                      {e?.nextServiceDate && <p className="text-xs text-warning">Próxima fecha: {new Date(e?.nextServiceDate)?.toLocaleDateString('es-MX')}</p>}
                      {e?.dueDate && <p className="text-xs text-warning">Vence: {new Date(e.dueDate)?.toLocaleDateString('es-MX')}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── ClientSolicitudesPanel ───────────────────────────────────────────────────
const URGENCY_CFG = {
  urgente: { label: 'Urgente', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500' },
  normal:  { label: 'Normal',  bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-200', dot: 'bg-gray-400' },
};
const STATUS_CFG = {
  nueva:        { label: 'Nueva',        bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  'en revisión':{ label: 'En revisión',  bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
  respondida:   { label: 'Respondida',   bg: 'bg-green-50',  text: 'text-green-700',  border: 'border-green-200' },
  cancelada:    { label: 'Cancelada',    bg: 'bg-red-50',    text: 'text-red-700',    border: 'border-red-200' },
  cerrada:      { label: 'Cerrada',      bg: 'bg-gray-100',  text: 'text-gray-600',   border: 'border-gray-200' },
};
const fmtFecha = (ts) => ts ? new Date(ts)?.toLocaleString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const ClientSolicitudesPanel = ({ clientId }) => {
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const { data } = await supabase
        ?.from('service_requests')
        ?.select(`id, request_type, description, urgency, status, notes, created_at, attended_at, read_at, origen, converted_to, vehicles(marca, modelo, year, placas)`)
        ?.eq('client_id', clientId)
        ?.order('created_at', { ascending: false })
        ?.limit(50);
      setSolicitudes(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (action, sol) => {
    setSaving(true);
    try {
      if (action === 'atender') {
        await supabase?.from('service_requests')?.update({ status: 'en revisión', attended_at: new Date()?.toISOString(), read_at: new Date()?.toISOString(), updated_at: new Date()?.toISOString() })?.eq('id', sol?.id);
      } else if (action === 'marcar_leida') {
        await supabase?.from('service_requests')?.update({ read_at: new Date()?.toISOString(), updated_at: new Date()?.toISOString() })?.eq('id', sol?.id);
      } else if (action === 'recepcion') {
        navigate('/vehicle-reception', { state: { fromSolicitud: true, solicitudId: sol?.id, clientId, vehicleId: sol?.vehicle_id, requestType: sol?.request_type, description: sol?.description } });
        return;
      } else if (action === 'cotizacion') {
        navigate('/quotation-management', { state: { fromSolicitud: true, solicitudId: sol?.id, clientId, vehicleId: sol?.vehicle_id, requestType: sol?.request_type, description: sol?.description } });
        return;
      }
      await load();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-8">
      <Icon name="Loader2" size={22} className="text-primary animate-spin" />
    </div>
  );

  if (solicitudes?.length === 0) return (
    <div className="text-center py-8">
      <Icon name="ClipboardList" size={32} className="text-muted-foreground mx-auto mb-2 opacity-40" />
      <p className="text-sm text-muted-foreground">Sin solicitudes para este cliente</p>
    </div>
  );

  return (
    <div className="space-y-3">
      {solicitudes?.map(sol => {
        const urg = URGENCY_CFG?.[sol?.urgency] || URGENCY_CFG?.normal;
        const sta = STATUS_CFG?.[sol?.status] || STATUS_CFG?.nueva;
        const isNueva = sol?.status === 'nueva';
        return (
          <div key={sol?.id} className={`rounded-lg border p-3 ${isNueva && !sol?.read_at ? 'border-orange-300 bg-orange-50/40' : 'border-border bg-background'}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-semibold text-foreground">{sol?.request_type}</p>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${sta?.bg} ${sta?.text} ${sta?.border}`}>{sta?.label}</span>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${urg?.bg} ${urg?.text} ${urg?.border}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${urg?.dot}`} />{urg?.label}
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                  <Icon name="Globe" size={9} />Portal Cliente
                </span>
              </div>
              <span className="text-xs text-muted-foreground flex-shrink-0">{fmtFecha(sol?.created_at)}</span>
            </div>
            {sol?.vehicles && (
              <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                <Icon name="Car" size={10} />
                {sol?.vehicles?.marca} {sol?.vehicles?.modelo} {sol?.vehicles?.year}{sol?.vehicles?.placas ? ` · ${sol?.vehicles?.placas}` : ''}
              </p>
            )}
            {sol?.description && <p className="text-xs text-foreground/70 bg-muted/40 rounded px-2 py-1 mb-2 line-clamp-2">{sol?.description}</p>}
            <div className="flex flex-wrap gap-1.5">
              {isNueva && (
                <button onClick={() => handleAction('atender', sol)} disabled={saving} className="flex items-center gap-1 px-2 py-1 rounded bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors disabled:opacity-60">
                  <Icon name="PlayCircle" size={11} />Atender
                </button>
              )}
              {!sol?.read_at && (
                <button onClick={() => handleAction('marcar_leida', sol)} disabled={saving} className="flex items-center gap-1 px-2 py-1 rounded bg-muted text-foreground text-xs font-medium hover:bg-muted/80 transition-colors disabled:opacity-60">
                  <Icon name="Eye" size={11} />Marcar leída
                </button>
              )}
              {!sol?.converted_to && (
                <>
                  <button onClick={() => handleAction('recepcion', sol)} disabled={saving} className="flex items-center gap-1 px-2 py-1 rounded bg-teal-100 text-teal-700 text-xs font-medium hover:bg-teal-200 transition-colors disabled:opacity-60">
                    <Icon name="ClipboardList" size={11} />A recepción
                  </button>
                  <button onClick={() => handleAction('cotizacion', sol)} disabled={saving} className="flex items-center gap-1 px-2 py-1 rounded bg-purple-100 text-purple-700 text-xs font-medium hover:bg-purple-200 transition-colors disabled:opacity-60">
                    <Icon name="FileText" size={11} />A cotización
                  </button>
                </>
              )}
              {sol?.converted_to && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-purple-50 text-purple-700 text-xs font-medium border border-purple-200">
                  <Icon name="CheckCircle2" size={11} />Convertida a {sol?.converted_to}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Main Page ───────────────────────────────────────────────────────────────
const ClientsManagement = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingList, setLoadingList] = useState(true);

  const [selectedClient, setSelectedClient] = useState(null);
  const [clientVehicles, setClientVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState(EMPTY_CLIENT);
  const [editErrors, setEditErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();

  // Admin reset password modal
  const [showResetModal, setShowResetModal] = useState(false);

  // Active tab for client detail
  const [clientDetailTab, setClientDetailTab] = useState('info');

  // New client form state
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [newClientData, setNewClientData] = useState(EMPTY_CLIENT);
  const [newClientErrors, setNewClientErrors] = useState({});
  const [savingNew, setSavingNew] = useState(false);
  const [newClientSaveError, setNewClientSaveError] = useState('');

  // Add vehicle modal state
  const [showAddVehicleModal, setShowAddVehicleModal] = useState(false);
  const [newVehicleData, setNewVehicleData] = useState(VEHICLE_FORM_DEFAULTS);
  const [newVehicleErrors, setNewVehicleErrors] = useState({});
  const [savingVehicle, setSavingVehicle] = useState(false);
  const [vehicleSaveError, setVehicleSaveError] = useState('');

  // Load clients list
  const loadClients = useCallback(async () => {
    setLoadingList(true);
    const data = await clientService?.getAll(500);
    setClients(data);
    setFilteredClients(data);
    setLoadingList(false);
  }, []);

  useEffect(() => { loadClients(); }, [loadClients]);

  // Filter clients
  useEffect(() => {
    if (!searchTerm?.trim()) {
      setFilteredClients(clients);
      return;
    }
    const term = searchTerm?.toLowerCase();
    setFilteredClients(
      clients?.filter(c =>
        c?.nombre?.toLowerCase()?.includes(term) ||
        c?.empresa?.toLowerCase()?.includes(term) ||
        c?.telefono?.includes(term) ||
        c?.email?.toLowerCase()?.includes(term) ||
        c?.rfc?.toLowerCase()?.includes(term)
      )
    );
  }, [searchTerm, clients]);

  // Select client
  const handleSelectClient = async (client) => {
    setLoadingDetail(true);
    setSelectedClient(client);
    setEditMode(false);
    setSelectedVehicle(null);
    setClientDetailTab('info');
    setEditData({
      nombre: client?.nombre || '',
      empresa: client?.empresa || '',
      telefono: client?.telefono || '',
      telefonoAlt: client?.telefonoAlt || '',
      email: client?.email || '',
      rfc: client?.rfc || '',
      direccion: client?.direccion || '',
      ciudad: client?.ciudad || '',
      estado: client?.estado || '',
      codigoPostal: client?.codigoPostal || ''
    });
    const vehicles = await clientService?.getVehicles(client?.id);
    setClientVehicles(vehicles);
    setLoadingDetail(false);
  };

  // Refresh vehicles for selected client
  const refreshClientVehicles = useCallback(async () => {
    if (!selectedClient?.id) return;
    const vehicles = await clientService?.getVehicles(selectedClient?.id);
    setClientVehicles(vehicles);
  }, [selectedClient?.id]);

  // Handle add vehicle
  const handleAddVehicle = () => {
    setNewVehicleData(VEHICLE_FORM_DEFAULTS);
    setNewVehicleErrors({});
    setVehicleSaveError('');
    setShowAddVehicleModal(true);
  };

  const handleSaveVehicle = async () => {
    if (savingVehicle) return;
    const errs = validateVehicleFields(newVehicleData);
    if (Object.keys(errs)?.length > 0) {
      setNewVehicleErrors(errs);
      return;
    }
    setSavingVehicle(true);
    setVehicleSaveError('');
    try {
      const companyId = await vehicleService?.getCompanyId();
      // Build payload with exact DB column names — no camelCase keys
      const vehiclePayload = {
        client_id: selectedClient?.id,
        company_id: companyId,
        tipo: newVehicleData?.vehicleType,
        placas: newVehicleData?.plates,
        marca: newVehicleData?.brand,
        modelo: newVehicleData?.model,
        year: newVehicleData?.year ? Number(newVehicleData?.year) : null,
        motor: newVehicleData?.engine || null,
        combustible: newVehicleData?.fuelType,
        color: newVehicleData?.color,
        vin: newVehicleData?.vin?.trim() || null,
      };
      await vehicleService?.createRaw(vehiclePayload);
      setShowAddVehicleModal(false);
      await refreshClientVehicles();
    } catch (e) {
      console.error('[handleSaveVehicle] error:', e);
      setVehicleSaveError(e?.message || 'Error al guardar la unidad. Intenta de nuevo.');
    } finally {
      setSavingVehicle(false);
    }
  };

  // Validate
  const validate = () => {
    const errs = {};
    if (!editData?.nombre?.trim()) errs.nombre = 'El nombre completo es obligatorio';
    if (editData?.email?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(editData?.email)) errs.email = 'Correo electrónico inválido';
    if (editData?.rfc?.trim() && !/^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/?.test(editData?.rfc?.toUpperCase())) errs.rfc = 'RFC inválido';
    setEditErrors(errs);
    return Object.keys(errs)?.length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const updated = await clientService?.update(selectedClient?.id, editData);
      setSelectedClient(updated);
      setClients(prev => prev?.map(c => c?.id === updated?.id ? updated : c));
      setFilteredClients(prev => prev?.map(c => c?.id === updated?.id ? updated : c));
      setEditMode(false);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setEditErrors({});
    setEditData({
      nombre: selectedClient?.nombre || '',
      empresa: selectedClient?.empresa || '',
      telefono: selectedClient?.telefono || '',
      telefonoAlt: selectedClient?.telefonoAlt || '',
      email: selectedClient?.email || '',
      rfc: selectedClient?.rfc || '',
      direccion: selectedClient?.direccion || '',
      ciudad: selectedClient?.ciudad || '',
      estado: selectedClient?.estado || '',
      codigoPostal: selectedClient?.codigoPostal || ''
    });
  };

  // Validate new client
  const validateNew = () => {
    const errs = {};
    if (!newClientData?.nombre?.trim()) errs.nombre = 'El nombre completo es obligatorio';
    if (!newClientData?.telefono?.trim()) errs.telefono = 'El teléfono es obligatorio';
    // email is optional — only validate format if provided
    if (newClientData?.email?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(newClientData?.email)) errs.email = 'Correo electrónico inválido';
    if (newClientData?.rfc?.trim() && !/^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/?.test(newClientData?.rfc?.toUpperCase())) errs.rfc = 'RFC inválido';
    setNewClientErrors(errs);
    return Object.keys(errs)?.length === 0;
  };

  const handleCreateClient = async () => {
    if (savingNew) return;
    if (!validateNew()) return;
    setSavingNew(true);
    setNewClientSaveError('');
    console.log('[handleCreateClient] START, data:', newClientData);
    try {
      const created = await clientService?.create(newClientData);
      console.log('[handleCreateClient] create returned:', created);
      if (created) {
        setClients(prev => [created, ...prev]);
        setFilteredClients(prev => [created, ...prev]);
        setNewClientData(EMPTY_CLIENT);
        setNewClientErrors({});
        setShowNewClientForm(false);
        handleSelectClient(created);
      } else {
        setNewClientSaveError('No se pudo guardar el cliente. Intenta de nuevo.');
      }
    } catch (e) {
      console.error('[handleCreateClient] error:', e);
      setNewClientSaveError(e?.message || 'Error al guardar el cliente. Intenta de nuevo.');
    } finally {
      console.log('[handleCreateClient] finally — resetting savingNew');
      setSavingNew(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <MainSidebar isCollapsed={isSidebarCollapsed} />
      <div className={`transition-smooth ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        {/* Header */}
        <header className="sticky top-0 z-30 bg-card border-b border-border shadow-elevation-1">
          <div className="flex items-center justify-between px-4 md:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <button
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                className="hidden lg:flex p-2 rounded-lg hover:bg-muted transition-smooth press-effect flex-shrink-0"
              >
                <Icon name={isSidebarCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={20} />
              </button>
              <div className="min-w-0 pl-10 lg:pl-0">
                <h1 className="text-xl font-heading font-semibold text-foreground leading-tight">Clientes</h1>
                <p className="text-sm caption text-muted-foreground mt-1">Ficha maestra de clientes y unidades</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                variant="default"
                size="sm"
                iconName="UserPlus"
                iconPosition="left"
                onClick={() => { setShowNewClientForm(true); setNewClientData(EMPTY_CLIENT); setNewClientErrors({}); setNewClientSaveError(''); }}
              >
                <span className="hidden sm:inline">Dar de alta cliente</span>
                <span className="sm:hidden sr-only">Nuevo</span>
              </Button>
            </div>
          </div>
        </header>

        <main className="px-4 md:px-6 lg:px-8 py-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

              {/* ── LEFT: Client List ── */}
              <div className="lg:col-span-1">
                <div className="bg-card rounded-lg border border-border shadow-elevation-1">
                  <div className="p-4 border-b border-border">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="p-1.5 bg-primary/10 rounded-lg">
                        <Icon name="Users" size={18} className="text-primary" />
                      </div>
                      <h2 className="text-base font-semibold text-foreground">Listado de Clientes</h2>
                      <span className="ml-auto text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{filteredClients?.length}</span>
                    </div>
                    <Input
                      type="text"
                      placeholder="Buscar por nombre, empresa, teléfono, email, RFC..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e?.target?.value)}
                      className="w-full"
                    />
                  </div>
                  <div className="overflow-y-auto max-h-[calc(100vh-260px)]">
                    {loadingList ? (
                      <div className="p-6 text-center">
                        <Icon name="Loader2" size={24} className="text-muted-foreground animate-spin mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">Cargando clientes...</p>
                      </div>
                    ) : filteredClients?.length === 0 ? (
                      <div className="p-6 text-center">
                        <Icon name="UserX" size={32} className="text-muted-foreground mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">No se encontraron clientes</p>
                      </div>
                    ) : (
                      filteredClients?.map(client => (
                        <button
                          key={client?.id}
                          onClick={() => handleSelectClient(client)}
                          className={`w-full text-left px-4 py-3 border-b border-border last:border-0 hover:bg-muted/50 transition-smooth ${
                            selectedClient?.id === client?.id ? 'bg-blue-50/60 border-l-4 border-l-blue-500 shadow-sm' : ''
                          }`}
                        >
                          <p className="text-sm font-medium text-foreground truncate">{client?.nombre}</p>
                          {client?.empresa && <p className="text-xs text-muted-foreground truncate">{client?.empresa}</p>}
                          <div className="flex items-center gap-3 mt-1">
                            {client?.telefono && (
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Icon name="Phone" size={10} />{client?.telefono}
                              </span>
                            )}
                            {client?.rfc && (
                              <span className="text-xs text-muted-foreground">{client?.rfc}</span>
                            )}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* ── RIGHT: Client Detail ── */}
              <div className="lg:col-span-2 space-y-6">
                {!selectedClient ? (
                  <div className="bg-card rounded-lg border border-border shadow-elevation-1 p-12 text-center">
                    <Icon name="UserSearch" size={48} className="text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-foreground mb-2">Selecciona un Cliente</h3>
                    <p className="text-sm text-muted-foreground">Elige un cliente del listado para ver su ficha completa.</p>
                  </div>
                ) : loadingDetail ? (
                  <div className="bg-card rounded-lg border border-border shadow-elevation-1 p-12 text-center">
                    <Icon name="Loader2" size={32} className="text-muted-foreground animate-spin mx-auto" />
                  </div>
                ) : (
                  <>
                    {/* ── DETAIL TABS ── */}
                    <div className="bg-card rounded-lg border border-border shadow-elevation-1">
                      {/* Tab bar */}
                      <div className="flex items-center gap-1 p-3 border-b border-border bg-muted/30 rounded-t-lg">
                        {[
                          { id: 'info', label: 'Información', icon: 'User' },
                          { id: 'vehiculos', label: 'Unidades', icon: 'Car' },
                          { id: 'solicitudes', label: 'Solicitudes', icon: 'ClipboardList' },
                        ]?.map(tab => (
                          <button
                            key={tab?.id}
                            onClick={() => setClientDetailTab(tab?.id)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                              clientDetailTab === tab?.id
                                ? 'bg-card shadow-sm text-foreground'
                                : 'text-muted-foreground hover:text-foreground'
                            }`}
                          >
                            <Icon name={tab?.icon} size={14} />
                            {tab?.label}
                          </button>
                        ))}
                      </div>

                      <div className="p-6">
                        {/* INFO TAB */}
                        {clientDetailTab === 'info' && (
                          <div>
                            <div className="flex items-center justify-between mb-6">
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-primary/10 rounded-lg">
                                  <Icon name="User" size={24} className="text-primary" />
                                </div>
                                <div>
                                  <h2 className="text-lg font-semibold text-foreground">Información del Cliente</h2>
                                  <p className="text-sm text-muted-foreground">
                                    {editMode ? 'Editando datos del cliente' : 'Datos del cliente seleccionado'}
                                  </p>
                                </div>
                              </div>
                              {!editMode ? (
                                <Button variant="outline" iconName="Pencil" iconPosition="left" onClick={() => setEditMode(true)}>
                                  Editar
                                </Button>
                              ) : (
                                <div className="flex gap-2">
                                  <Button variant="outline" onClick={handleCancel} disabled={saving}>Cancelar</Button>
                                  <Button variant="default" iconName="Save" iconPosition="left" loading={saving} onClick={handleSave}>Guardar</Button>
                                </div>
                              )}
                            </div>
                            <ClientInfoForm
                              data={editMode ? editData : {
                                nombre: selectedClient?.nombre,
                                empresa: selectedClient?.empresa,
                                telefono: selectedClient?.telefono,
                                telefonoAlt: selectedClient?.telefonoAlt,
                                email: selectedClient?.email,
                                rfc: selectedClient?.rfc,
                                direccion: selectedClient?.direccion,
                                ciudad: selectedClient?.ciudad,
                                estado: selectedClient?.estado,
                                codigoPostal: selectedClient?.codigoPostal
                              }}
                              onChange={(key, val) => setEditData(p => ({ ...p, [key]: val }))}
                              errors={editErrors}
                              readOnly={!editMode}
                            />

                            {/* ── Restablecer contraseña — solo admin/dirección ── */}
                            {['admin', 'direccion']?.includes(userProfile?.role) && !editMode && (
                              <div className="mt-6 pt-5 border-t border-border">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-sm font-semibold text-foreground flex items-center gap-2">
                                      <Icon name="KeyRound" size={15} className="text-orange-500" />
                                      Acceso al Portal Cliente
                                    </p>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                      {selectedClient?.portal_user_id
                                        ? 'Cuenta auth vinculada' :'Sin cuenta auth registrada'}
                                    </p>
                                  </div>
                                  <button
                                    onClick={() => setShowResetModal(true)}
                                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-orange-50 text-orange-700 hover:bg-orange-100 border border-orange-200 transition-smooth"
                                  >
                                    <Icon name="KeyRound" size={14} />
                                    Restablecer contraseña
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* VEHICULOS TAB */}
                        {clientDetailTab === 'vehiculos' && (
                          <div>
                            <div className="flex items-center justify-between mb-5">
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-accent/10 rounded-lg">
                                  <Icon name="Car" size={22} className="text-accent" />
                                </div>
                                <div>
                                  <h2 className="text-lg font-semibold text-foreground">Unidades del Cliente</h2>
                                  <p className="text-sm text-muted-foreground">{clientVehicles?.length} unidad(es) registrada(s)</p>
                                </div>
                              </div>
                              <Button
                                variant="default"
                                size="sm"
                                iconName="Plus"
                                iconPosition="left"
                                onClick={handleAddVehicle}
                              >
                                Agregar unidad
                              </Button>
                            </div>
                            {clientVehicles?.length === 0 ? (
                              <div className="text-center py-6">
                                <Icon name="Car" size={32} className="text-muted-foreground mx-auto mb-2" />
                                <p className="text-sm text-muted-foreground">Sin unidades registradas para este cliente.</p>
                              </div>
                            ) : (
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {clientVehicles?.map(v => (
                                  <button
                                    key={v?.id}
                                    onClick={() => setSelectedVehicle(selectedVehicle?.id === v?.id ? null : v)}
                                    className={`text-left p-4 rounded-lg border transition-smooth hover:shadow-elevation-1 ${
                                      selectedVehicle?.id === v?.id
                                        ? 'border-primary bg-primary/5' : 'border-border bg-background hover:bg-muted/30'
                                    }`}
                                  >
                                    <div className="flex items-center gap-2 mb-2">
                                      <Icon name="Car" size={16} className="text-primary" />
                                      <span className="text-sm font-semibold text-foreground">
                                        {v?.marca} {v?.modelo} {v?.año || v?.year}
                                      </span>
                                    </div>
                                    <div className="space-y-1">
                                      {v?.placas && (
                                        <p className="text-xs text-muted-foreground">
                                          <span className="font-medium">Placas:</span> {v?.placas}
                                        </p>
                                      )}
                                      {v?.vin && (
                                        <p className="text-xs text-muted-foreground font-mono">
                                          <span className="font-medium font-sans">VIN:</span> {v?.vin}
                                        </p>
                                      )}
                                      {v?.color && (
                                        <p className="text-xs text-muted-foreground">
                                          <span className="font-medium">Color:</span> {v?.color}
                                        </p>
                                      )}
                                    </div>
                                    <p className="text-xs text-primary mt-2">
                                      {selectedVehicle?.id === v?.id ? 'Ocultar historial ▲' : 'Ver historial ▼'}
                                    </p>
                                    <button
                                      onClick={(e) => { e?.stopPropagation(); navigate(`/vehicle-history/${v?.id}`); }}
                                      className="mt-2 text-xs text-white bg-primary hover:bg-primary/90 px-3 py-1 rounded-md transition-colors w-full text-center"
                                    >
                                      Ver Historial del Vehículo
                                    </button>
                                  </button>
                                ))}
                              </div>
                            )}
                            {selectedVehicle && (
                              <div className="mt-5 pt-5 border-t border-border">
                                <div className="flex items-center gap-2 mb-4">
                                  <Icon name="History" size={18} className="text-primary" />
                                  <h3 className="text-base font-semibold text-foreground">
                                    Historial: {selectedVehicle?.marca} {selectedVehicle?.modelo} — {selectedVehicle?.placas}
                                  </h3>
                                </div>
                                <VehicleHistoryPanel vehicleId={selectedVehicle?.id} />
                              </div>
                            )}
                          </div>
                        )}

                        {/* SOLICITUDES TAB */}
                        {clientDetailTab === 'solicitudes' && (
                          <div>
                            <div className="flex items-center gap-3 mb-5">
                              <div className="p-2 bg-orange-100 rounded-lg">
                                <Icon name="ClipboardList" size={22} className="text-orange-600" />
                              </div>
                              <div>
                                <h2 className="text-lg font-semibold text-foreground">Solicitudes del Cliente</h2>
                                <p className="text-sm text-muted-foreground">Solicitudes enviadas desde el Portal Cliente</p>
                              </div>
                            </div>
                            <ClientSolicitudesPanel clientId={selectedClient?.id} />
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </main>

        {/* ── New Client Modal ── */}
        {showNewClientForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-card rounded-xl border border-border shadow-elevation-3 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-5 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Icon name="UserPlus" size={20} className="text-primary" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">Dar de alta cliente</h2>
                    <p className="text-sm text-muted-foreground">Registra un nuevo cliente en el sistema</p>
                  </div>
                </div>
                <button
                  onClick={() => { setShowNewClientForm(false); setNewClientErrors({}); setNewClientSaveError(''); }}
                  className="p-2 rounded-lg hover:bg-muted transition-smooth"
                >
                  <Icon name="X" size={18} />
                </button>
              </div>
              <div className="p-5">
                <ClientInfoForm
                  data={newClientData}
                  onChange={(key, val) => setNewClientData(prev => ({ ...prev, [key]: val }))}
                  errors={newClientErrors}
                />
              </div>
              <div className="flex gap-3 justify-end p-5 border-t border-border">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setShowNewClientForm(false); setNewClientErrors({}); setNewClientSaveError(''); }}
                >
                  Cancelar
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  loading={savingNew}
                  iconName="UserPlus"
                  iconPosition="left"
                  onClick={handleCreateClient}
                >
                  Dar de alta
                </Button>
              </div>
              {newClientSaveError && (
                <div className="px-5 pb-4">
                  <p className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">{newClientSaveError}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Add Vehicle Modal ── */}
        {showAddVehicleModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-card rounded-xl border border-border shadow-elevation-3 w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-5 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Icon name="Car" size={20} className="text-primary" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">Agregar unidad</h2>
                    <p className="text-sm text-muted-foreground">{selectedClient?.nombre}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAddVehicleModal(false)}
                  className="p-2 rounded-lg hover:bg-muted transition-smooth"
                >
                  <Icon name="X" size={18} />
                </button>
              </div>
              <div className="p-5">
                <VehicleFormUnified
                  formData={newVehicleData}
                  onChange={(field, value) => setNewVehicleData(prev => ({ ...prev, [field]: value }))}
                  errors={newVehicleErrors}
                />
              </div>
              {vehicleSaveError && (
                <div className="px-5 pb-2">
                  <p className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">{vehicleSaveError}</p>
                </div>
              )}
              <div className="flex gap-3 justify-end p-5 border-t border-border">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAddVehicleModal(false)}
                  disabled={savingVehicle}
                >
                  Cancelar
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  loading={savingVehicle}
                  iconName="Save"
                  iconPosition="left"
                  onClick={handleSaveVehicle}
                >
                  Guardar unidad
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ── Admin Reset Password Modal ── */}
        {showResetModal && selectedClient && (
          <AdminResetPasswordModal
            client={{
              id: selectedClient?.id,
              nombre: selectedClient?.nombre,
              email: selectedClient?.email,
              telefono: selectedClient?.telefono,
              portal_user_id: selectedClient?.portal_user_id,
            }}
            portalAccessRequestId={null}
            companyId={userProfile?.company_id}
            performedBy={{ id: user?.id, nombre: userProfile?.nombre || user?.email }}
            onClose={() => setShowResetModal(false)}
            onSuccess={() => {
              setShowResetModal(false);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ClientsManagement;
